// prototypes/budgetSpec.ts (v2.19)
// 「予算・前年比較」 タブ専用: 6 列 × 3 行 = 18 指標 (Cards/Charts) + 2 指標 (表のみ) = 計 20 指標 spec。
// 16 base 指標 + 4 派生指標。 派生は dept_metrics_all の base 値から client 側で計算。
//
// 派生 4 指標:
//   - 薬剤材料費                  = 出来高換算薬剤費 + 出来高換算材料費                     (千円) — 表のみ
//   - 入院患者延数/医師1名あたり    = 入院患者延数 / 常勤換算数 / 月日数         (人)   — 1日・1名平均
//   - 在院患者延数/医師1名あたり    = 在院患者延数 / 常勤換算数 / 月日数         (人)   — 1日・1名平均
//   - 1日粗利単価(在院)           = GP / 在院患者延数 × 1000                    (円/日)
//
// 月日数で割る派生指標は scope (current/prevMonth/prevYear/budget/trend 各 date) の月日数で割るため、
// MetricSpec.derive.compute は date 文字列を含む context を受け取る。
//
// tableOnly: 表にだけ載せる指標 (Cards/Charts grid からは除外)。 現在は 薬剤材料費 / 病床利用率。
import type { Direction, DeptMetricsAllResponse, DeptMetricsTrendPoint } from "@shared/api";
import { gpText } from "../../lib/gpMode";

export interface ComputeContext { date: string; }

export interface MetricSpec {
  key:        string;         // 内部 key (= base 集計区分名 or 派生 key)
  label:      string;         // 表示名
  unit:       string;
  direction:  Direction;
  description?: string;
  tableOnly?: boolean;        // true なら Cards/Charts grid から除外、 表にのみ表示
  base?:      string;         // base 指標 (派生でない場合)
  derive?: {
    deps:    string[];        // 必要な base 集計区分名
    /** 各 base 値 (Map) と scope の date から派生値を計算 */
    compute: (vals: Map<string, number | null>, ctx: ComputeContext) => number | null;
  };
}

const diffOrNull = (a: number | null | undefined, b: number | null | undefined): number | null =>
  (a == null || b == null || !Number.isFinite(a) || !Number.isFinite(b)) ? null : a - b;

const sumOrNull = (...xs: (number | null | undefined)[]): number | null => {
  // null/undefined が 1 つでもあれば結果も null (両方欠損の合算は意味がない)
  let acc = 0;
  for (const x of xs) {
    if (x == null || !Number.isFinite(x)) return null;
    acc += x;
  }
  return acc;
};
const ratioOrNull = (num: number | null | undefined, den: number | null | undefined, scale = 1): number | null => {
  if (num == null || den == null || !Number.isFinite(num) || !Number.isFinite(den) || den === 0) return null;
  return (num / den) * scale;
};
/** "YYYY-MM-DD" から その月の日数 (28-31) を返す。 月末取得は new Date(y, m, 0) を使う */
function daysInMonth(yyyymmdd: string): number {
  const [y, m] = yyyymmdd.split("-").map(Number);
  return new Date(Date.UTC(y, m, 0)).getUTCDate();
}
/** "YYYY-MM-DD" を delta ヶ月シフト (store.ts の monthShift と同等、 import 循環避けるため inline) */
function monthShift(yyyymmdd: string, delta: number): string {
  const [y, m, d] = yyyymmdd.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1 + delta, d));
  return [
    dt.getUTCFullYear(),
    String(dt.getUTCMonth() + 1).padStart(2, "0"),
    String(dt.getUTCDate()).padStart(2, "0"),
  ].join("-");
}

// 配列順 = 表の行順。 Cards/Charts は tableOnly=false の指標だけを並び順そのままに 6×3 = 18 表示。
// 表は 18 + tableOnly 2 = 20 行。
// [開発版(新GP) 2026-08-24] label は gpText() を通す。ここが 要確認診療科 / 相関分析 /
//   入院実績 / KPIカード など **ほぼ全タブの指標名の出所**なので、ここを直せば一括で揃う。
export const BUDGET_TAB_SPECS: MetricSpec[] = [
  // ─ Card Row 1: 収入 / 粗利 / 入院延数 / 在院延数 / DPCⅡ / 常勤医師数 ─
  { key: "入院収入",         label: "入院収入",       unit: "千円", direction: "higher_is_better", base: "入院収入" },
  { key: "GP",               label: gpText("粗利"),  unit: "千円", direction: "higher_is_better", base: "GP" },
  { key: "入院患者延数",      label: "入院患者延数",    unit: "人日", direction: "higher_is_better", base: "入院患者延数" },
  { key: "在院患者延数",      label: "在院患者延数",    unit: "人日", direction: "higher_is_better", base: "在院患者延数" },
  { key: "DPC期間2以内率",   label: "DPCⅡ以内率",   unit: "%",    direction: "higher_is_better", base: "DPC期間2以内率" },
  { key: "常勤換算数",       label: "常勤医師数(入院診療科)",     unit: "人",   direction: "higher_is_better", base: "常勤換算数" },

  // ─ Card Row 2: 入院延数/医師1名 / 1日入院単価 / 1日粗利単価 / 在院延数/医師1名 / 1日在院単価 / 1日粗利単価(在院) ─
  { key: "入院患者延数_医師1名", label: "1日入院患者延数/医師1名あたり", unit: "人", direction: "higher_is_better",
    derive: { deps: ["入院患者延数", "常勤換算数"],
              // 1日・1名 平均: 入院患者延数 (人日) ÷ 常勤換算数 (人) ÷ 月日数 (日) → 人
              compute: (m, ctx) => ratioOrNull(m.get("入院患者延数"), m.get("常勤換算数"), 1 / daysInMonth(ctx.date)) } },
  { key: "入院単価",         label: "1日入院単価",    unit: "円/日", direction: "higher_is_better", base: "入院単価" },
  { key: "GP単価",           label: gpText("1日粗利単価"), unit: "円/日", direction: "higher_is_better", base: "GP単価" },
  { key: "在院患者延数_医師1名", label: "1日在院患者延数/医師1名あたり", unit: "人", direction: "higher_is_better",
    derive: { deps: ["在院患者延数", "常勤換算数"],
              compute: (m, ctx) => ratioOrNull(m.get("在院患者延数"), m.get("常勤換算数"), 1 / daysInMonth(ctx.date)) } },
  { key: "在院単価",         label: "1日在院単価", unit: "円/日", direction: "higher_is_better", base: "在院単価" },
  { key: "GP単価_在院",      label: gpText("1日粗利単価(在院)"), unit: "円/日", direction: "higher_is_better",
    derive: { deps: ["GP", "在院患者延数"],
              // GP は 千円、 在院患者延数 は 人日 → 円/日 にするため ×1000
              compute: (m) => ratioOrNull(m.get("GP"), m.get("在院患者延数"), 1000) } },

  // ─ Card Row 3: 新規入院 / 新規受持 / 退院 / 平均在院日数 / 予定入院率 / 紹介入院率 ─
  { key: "新規入院数",       label: "新規入院数",     unit: "人",   direction: "higher_is_better", base: "新規入院数" },
  { key: "新規受持数",       label: "新規受持数",     unit: "人",   direction: "higher_is_better", base: "新規受持数" },
  { key: "退院患者数",       label: "退院患者数",     unit: "人",   direction: "higher_is_better", base: "退院患者数" },
  { key: "平均在院日数",     label: "平均在院日数",    unit: "日",   direction: "lower_is_better",  base: "平均在院日数" },
  { key: "予定入院率",       label: "予定入院率",     unit: "%",    direction: "higher_is_better", base: "予定入院率" },
  { key: "紹介入院率",       label: "紹介入院率",     unit: "%",    direction: "higher_is_better", base: "紹介入院率" },

  // ─ 表のみ (Cards/Charts には出さない) ─
  { key: "出来高換算薬剤材料費",       label: "出来高換算薬剤材料費",     unit: "千円", direction: "lower_is_better",  tableOnly: true,
    derive: { deps: ["出来高換算薬剤費", "出来高換算材料費"],
              compute: (m) => sumOrNull(m.get("出来高換算薬剤費"), m.get("出来高換算材料費")) } },
  { key: "病床利用率",       label: "病床利用率",     unit: "%",    direction: "higher_is_better", tableOnly: true, base: "病床利用率" },
];

// 入院実績(前年度・予算比較) βタブ専用の追加指標 (2026-07-10)。
// BUDGET_TAB_SPECS には入れない (入院実績タブ / モバイル / 相関分析の表示を変えないため)。
// 手術収入/手術件数 は server dept_metrics_all が fact_kb_revenue_long / fact_surgery_small_long
// から合成して返す (2026-07-14: 医療区分は急性期/回復期にも対応。予定緊急=合計のときのみ)。
// 予定入院数/他科から転科 は fact_long_all 由来。
export const COMPARE_EXTRA_SPECS: MetricSpec[] = [
  // 実体 = 行為別集計の「手術小計」(手術手技+麻酔+輸血+手術材料/薬剤)。表示名は 手術麻酔収入 に統一
  // (2026-07-14 ユーザ指示・月別/科別マトリックスで同名)
  { key: "手術収入",     label: "手術麻酔収入",   unit: "千円", direction: "higher_is_better", base: "手術収入" },
  { key: "手術件数",     label: "手術室件数",     unit: "件",   direction: "higher_is_better", base: "手術件数" },
  { key: "予定入院数",   label: "予定入院数",     unit: "人",   direction: "higher_is_better", base: "予定入院数" },
  { key: "緊急入院率",   label: "緊急入院率",     unit: "%",   direction: "higher_is_better", base: "緊急入院率" },
  // 他科からの転科 / 内、専攻医数 も 前年度比・予算比 で背景色を付ける (2026-07-14 ユーザ指示)。
  // direction=neutral だと cellBg が無色を返して 1 行だけ色が付かなかった。
  // 増=薄青 / 減=薄赤 (他指標と同じ「達成=青・未達=赤」の読み方に揃える)。
  { key: "他科から転科", label: "他科からの転科", unit: "人",   direction: "higher_is_better", base: "他科から転科" },
  { key: "内専攻医常勤換算数", label: "内、専攻医数", unit: "人", direction: "higher_is_better", base: "内専攻医常勤換算数" },
  // 病院全体の月別マトリックス専用 (2026-07-13): 延数基準=入院 のとき表示。在院のときは既存の 病床利用率 spec (tableOnly) を使う
  { key: "病床稼働率",   label: "病床稼働率",     unit: "%",    direction: "higher_is_better", base: "病床稼働率" },
];

// GP分析タブの先頭カード (2026-09-10 ユーザ指示)。
//   入院収入 → 薬剤材料費(出来高換算額) → 医師人件費 → GP の順で、GP の作り方をそのまま並べる。
//   GP = 入院収入 − 出来高換算薬剤費 − 出来高換算材料費 − 医師人件費 (lib/gpMode.ts の THETA_GP_DEF)。
//
// ★「薬剤材料費(出来高換算額)」は単独の集計区分として存在しない。
//   `出来高換算薬剤費 + 出来高換算材料費` と `入院収入 − 薬剤材料費差引後入院収入` は
//   実測で完全一致する (2026-09-10・202606-202608 の 418,288.9 / 401,799.7 / 375,055.6 千円) が、
//   **薬剤費・材料費の側には予算が無い** (dim_metric.has_budget=FALSE) ので、
//   予算比・前年比まで出せる後者の式を採る (入院収入・薬剤材料費差引後入院収入は両方 has_budget=TRUE)。
export const GP_TAB_CARD_SPECS: MetricSpec[] = [
  { key: "入院収入", label: "入院収入", unit: "千円", direction: "higher_is_better", base: "入院収入" },
  { key: "薬剤材料費(出来高換算額)", label: "薬剤材料費(出来高換算額)", unit: "千円",
    direction: "lower_is_better",
    description: "入院収入 − 薬剤材料費差引後入院収入。使った薬剤・材料を出来高評価した額 (EF の包括前) で、"
      + "DPC 包括期間に実際には請求できなかった分も含む = 償還額ではない。",
    derive: {
      deps: ["入院収入", "薬剤材料費差引後入院収入"],
      compute: (v) => diffOrNull(v.get("入院収入"), v.get("薬剤材料費差引後入院収入")),
    } },
  { key: "医師人件費", label: "医師人件費", unit: "千円", direction: "lower_is_better", base: "医師人件費" },
  { key: "GP", label: "GP", unit: "千円", direction: "higher_is_better", base: "GP" },
];

// 入院KPI タブで使う 13 指標 (注目指標・月別マトリックス・科別実績の絞り込み + KpiBigPanel カード)。
// すべて BUDGET_TAB_SPECS の key の部分集合 (派生指標は含めない)。FilterBar / KpiBigPanel 共有。
export const KPI_TAB_METRIC_KEYS = new Set<string>([
  "入院収入", "GP", "入院患者延数", "在院患者延数",
  "入院単価", "在院単価", "GP単価", "常勤換算数",
  "新規入院数", "新規受持数", "紹介入院率", "平均在院日数", "DPC期間2以内率",
]);

// 12ヶ月移動平均 trend: 各点 = その月までの直近 window ヶ月の単純平均 (actual/budget/prevYear 各々)。
// 窓 (window ヶ月) がそろう点だけ返す (= 末尾 len-window+1 点)。 24ヶ月入力 → 13点。
// グラフ・マトリックスのセルを「直近12ヶ月平均」表示にするのに使う (集計列の平均は別途 raw から算出)。
//
// skipZero=true (GP/単価/薬剤費 系の SKIP_ZERO_KEYS 指標): 0 を「未確定/未設定」とみなして
//   平均母数から除外する。これをしないと、 予算未設定月 (例: FY2025 の 1日粗利単価予算=0) を 0 として
//   平均してしまい、 予算線・予算セルが不当に低くなる (2026-06-19 修正)。
//   ※ count 系 (件数等) で 0 が正当値の指標は skipZero=false のままにすること。
export function movingAvgTrend(trend: DeptMetricsTrendPoint[], window = 12, skipZero = false): DeptMetricsTrendPoint[] {
  const ma = (sel: (p: DeptMetricsTrendPoint) => number | null, i: number): number | null => {
    const vals: number[] = [];
    for (let k = i - window + 1; k <= i; k++) {
      const v = sel(trend[k]);
      if (v != null && Number.isFinite(v) && !(skipZero && v === 0)) vals.push(v);
    }
    return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
  };
  const out: DeptMetricsTrendPoint[] = [];
  for (let i = window - 1; i < trend.length; i++) {
    out.push({
      date:     trend[i].date,
      actual:   ma((p) => p.actual,   i),
      budget:   ma((p) => p.budget,   i),
      prevYear: ma((p) => p.prevYear, i),
    });
  }
  return out;
}

// dept_metrics_all のレスポンスから「key 別の単月値 + trend」 を spec ベースで解決
export interface ResolvedMetric {
  spec:      MetricSpec;
  current:   number | null;
  prevMonth: number | null;
  prevYear:  number | null;
  budget:    number | null;
  trend:     DeptMetricsTrendPoint[];
}

/** dept_metrics_all 全行の base lookup map を作る (派生計算用) */
function buildLookup(data: DeptMetricsAllResponse) {
  const byMetric = new Map(data.rows.map((r) => [r.集計区分, r]));
  return byMetric;
}

export function resolveMetric(spec: MetricSpec, data: DeptMetricsAllResponse): ResolvedMetric {
  const lookup = buildLookup(data);

  // base 指標 → そのまま row を引く
  if (spec.base) {
    const row = lookup.get(spec.base);
    return {
      spec,
      current:   row?.current   ?? null,
      prevMonth: row?.prevMonth ?? null,
      prevYear:  row?.prevYear  ?? null,
      budget:    row?.budget    ?? null,
      trend:     row?.trend     ?? data.trendMonths.map((d) => ({ date: d, actual: null, budget: null, prevYear: null })),
    };
  }

  // 派生指標 → deps の各値を引いて compute(date context 付き)
  const deriv = spec.derive!;
  const depRows = deriv.deps.map((d) => lookup.get(d));

  const valAt = (sel: (r: typeof depRows[number]) => number | null | undefined, scopeDate: string): number | null => {
    const m = new Map<string, number | null>();
    deriv.deps.forEach((name, i) => m.set(name, sel(depRows[i]) ?? null));
    return deriv.compute(m, { date: scopeDate });
  };

  // 各 scope は対応する date 文字列を渡す:
  //   current   → asOfMonth
  //   prevMonth → asOfMonth - 1M
  //   prevYear  → asOfMonth - 12M  (前年同月の日数 = April 30 / February 28-29 等)
  //   budget    → asOfMonth        (予算は対象月の予算)
  const asOf       = data.asOfMonth;
  const prevMoDate = monthShift(asOf, -1);
  const prevYrDate = monthShift(asOf, -12);
  const current   = valAt((r) => r?.current,   asOf);
  const prevMonth = valAt((r) => r?.prevMonth, prevMoDate);
  const prevYear  = valAt((r) => r?.prevYear,  prevYrDate);
  const budget    = valAt((r) => r?.budget,    asOf);

  // trend: 各 date で deps の値を集めて compute (prevYear は -12M した date を context に)
  const trend: DeptMetricsTrendPoint[] = data.trendMonths.map((d, di) => {
    const acc: Record<"actual" | "budget" | "prevYear", Map<string, number | null>> = {
      actual:   new Map(),
      budget:   new Map(),
      prevYear: new Map(),
    };
    deriv.deps.forEach((name, ix) => {
      const tp = depRows[ix]?.trend[di];
      acc.actual  .set(name, tp?.actual   ?? null);
      acc.budget  .set(name, tp?.budget   ?? null);
      acc.prevYear.set(name, tp?.prevYear ?? null);
    });
    return {
      date:     d,
      actual:   deriv.compute(acc.actual,   { date: d }),
      budget:   deriv.compute(acc.budget,   { date: d }),
      prevYear: deriv.compute(acc.prevYear, { date: monthShift(d, -12) }),
    };
  });

  return { spec, current, prevMonth, prevYear, budget, trend };
}

export function resolveAllSpecs(data: DeptMetricsAllResponse): ResolvedMetric[] {
  return BUDGET_TAB_SPECS.map((s) => resolveMetric(s, data));
}
